import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import Breadcrums from "../../common/breadcrumbs";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { handleServerValidations } from "../../../../utils/commonfunction";
import { Update, Details } from "../../services/reportPrice.services";
import { List as ReportList } from "../../services/report.services";

const EditReportPrice = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const [reports, setReports] = useState([]);
    const [initialValues, setInitialValues] = useState({
        report_id: "",
        about: "",
        duration: "",
        price_inr: "",
        price_usd: ""
    });

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        { title: "Report Prices", url: "/admin/report-price/list/1" },
        { title: "Edit Price", url: "" },
    ];

    useEffect(() => {
        // Fetch active reports
        const formData = new FormData();
        formData.append("per_page", 1000);
        formData.append("search", JSON.stringify({ status: "Active" }));
        
        ReportList(formData)
            .then(res => {
                if (res?.data?.list) {
                    setReports(res.data.list);
                }
            })
            .catch(err => console.log(err));

        // Fetch price details
        if (id) {
            Details(id)
                .then(res => {
                    if (res?.data) {
                        setInitialValues({
                            report_id: res.data.report_id || "",
                            about: res.data.about || "",
                            duration: res.data.duration || "",
                            price_inr: res.data.price_inr || "",
                            price_usd: res.data.price_usd || ""
                        });
                    }
                })
                .catch(err => console.log(err));
        }
    }, [id]);

    const validationSchema = Yup.object().shape({
        report_id: Yup.string().required("Report is required"),
        about: Yup.string().optional(),
        duration: Yup.string().required("Duration is required"),
        price_inr: Yup.number().required("INR Price is required").min(0, "Price must be positive"),
        price_usd: Yup.number().required("USD Price is required").min(0, "Price must be positive")
    });

    const handleSubmit = (values, { setSubmitting }) => {
        const payload = { ...values, id };
        Update(payload)
            .then((response) => {
                setSubmitting(false);
                if (response.status || response.success) {
                    Swal.fire({
                        icon: "success",
                        text: response.message,
                        ...SWAL_SETTINGS,
                    });
                    navigate("/admin/report-price/list/1");
                } else {
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(response),
                        ...SWAL_SETTINGS,
                    });
                }
            })
            .catch((error) => {
                setSubmitting(false);
                Swal.fire({
                    icon: "error",
                    text: handleServerValidations(error),
                    ...SWAL_SETTINGS,
                });
                console.log(error);
            });
    };

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between mb-3">
                            <h6 className="main-content-label">Edit Report Price</h6>
                            <Link to="/admin/report-price/list/1" className="btn btn-warning signbtn ripple">
                                <i className="fa fa-arrow-left"></i> {t("btn_back")}
                            </Link>
                        </div>
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                            enableReinitialize={true}
                        >
                            {({ isSubmitting }) => (
                                <Form className="p-3">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <div className="form-group">
                                                <label>Select Report <span className="text-danger">*</span></label>
                                                <Field as="select" name="report_id" className="form-control">
                                                    <option value="">Select an active report</option>
                                                    {reports.map((report) => (
                                                        <option key={report.id || report._id} value={report.id || report._id}>
                                                            {report.title}
                                                        </option>
                                                    ))}
                                                </Field>
                                                <ErrorMessage name="report_id" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-md-3">
                                            <div className="form-group mb-0">
                                                <label>About</label>
                                                <Field name="about" className="form-control" placeholder="Enter about info" />
                                                <ErrorMessage name="about" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-group mb-0">
                                                <label>Duration <span className="text-danger">*</span></label>
                                                <Field name="duration" className="form-control" placeholder="e.g. 1 Year, 6 Months" />
                                                <ErrorMessage name="duration" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-group mb-0">
                                                <label>Price (INR) <span className="text-danger">*</span></label>
                                                <Field name="price_inr" type="number" className="form-control" placeholder="INR" />
                                                <ErrorMessage name="price_inr" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-group mb-0">
                                                <label>Price (USD) <span className="text-danger">*</span></label>
                                                <Field name="price_usd" type="number" className="form-control" placeholder="USD" />
                                                <ErrorMessage name="price_usd" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mt-4">
                                        <div className="col-md-12 text-center">
                                            <button type="submit" className="btn btn-main-primary signbtn mr-2" disabled={isSubmitting}>
                                                {isSubmitting ? "Saving..." : "Update Price"}
                                            </button>
                                        </div>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditReportPrice;
